import featureGuestRsvp from '../../Assets/landing/features/rsvp.png'
import featureWebsite from '../../Assets/landing/features/website.png'
import featureTablePlan from '../../Assets/landing/features/table-plan.webp'
import featureSchedule from '../../Assets/landing/features/schedule.png'
import featureChecklist from '../../Assets/landing/features/todo-list.png'
import featureGuestGallery from '../../Assets/landing/features/guest-gallery.webp'
import featureSaveTheDate from '../../Assets/landing/features/save-the-date.png'
import featureCouplePlanning from '../../Assets/landing/features/couple-planning.webp'
import mealIcon from '../../Assets/Icons/meal.png'

/**
 * Content for every marketing feature page. Each entry drives a page at
 * /features/<slug> plus the cards on /features and the landing page slider.
 */
const FEATURES = [
    {
        slug: 'guest-list',
        name: 'Guest List Manager',
        plan: 'Free',
        tagline: 'Every guest, plus-one and dietary note in one place',
        seoTitle: 'Wedding Guest List App UK | First Dance',
        seoDescription:
            'Manage your wedding guest list from your phone. Track plus-ones, dietary requirements, day and evening invitations and RSVP responses in the free First Dance app.',
        image: featureCouplePlanning,
        imageAlt: 'Couple managing their wedding guest list together in the First Dance app',
        heroIntro:
            'Your guest list touches everything: numbers for the venue, meals for the caterer, seats at every table. First Dance keeps it all in one place, so you only ever add a guest once.',
        benefits: [
            {
                title: 'Add guests once, use them everywhere',
                text: 'Every guest you add flows straight through to your RSVPs, table plan and meal choices — no re-typing names into spreadsheets.',
            },
            {
                title: 'Plus-ones and children handled',
                text: 'Attach plus-ones to any guest, mark adults, children and babies, and note who needs a high chair on the day.',
            },
            {
                title: 'Day and evening invitations',
                text: 'Invite some guests to the whole day and others to the evening reception — meal choices are only asked of those who need them.',
            },
            {
                title: 'Dietary requirements captured',
                text: 'Vegetarian, vegan, gluten free or anything else — dietary notes stay with each guest and export neatly for your caterer.',
            },
            {
                title: 'Download your data',
                text: 'Export your guest list and meal choices as a CSV whenever your venue or caterer asks for final numbers.',
            },
        ],
        howItWorks: [
            'Download First Dance and create your free account as a couple.',
            'Add your guests with names, plus-ones and who they’re a guest of.',
            'Choose day or evening invitations for each guest.',
            'Watch RSVPs, meals and seating update automatically as you plan.',
        ],
        faqs: [
            {
                q: 'Is the guest list free to use?',
                a: 'Yes — the guest list is part of the free First Dance plan, with no guest limit. You can add everyone from the very first day of planning.',
            },
            {
                q: 'Can I track who has and hasn’t replied?',
                a: 'Yes. Your dashboard shows accepted, declined and awaiting counts at a glance, and you can filter the list by RSVP status.',
            },
            {
                q: 'Can I export my guest list?',
                a: 'You can download your guest data, including meal choices and dietary requirements, as a CSV to share with your venue or caterer.',
            },
        ],
        related: ['rsvp', 'table-planner', 'menu-planner'],
    },
    {
        slug: 'rsvp',
        name: 'Digital RSVPs',
        plan: 'Premium',
        tagline: 'Invites and RSVPs — sorted!',
        seoTitle: 'Digital Wedding RSVP App UK | First Dance',
        seoDescription:
            'Send secure digital RSVP links to your wedding guests and collect responses, meal choices and dietary needs automatically. RSVP tracking made fun with First Dance.',
        image: featureGuestRsvp,
        imageAlt: 'Digital wedding RSVP tracking in the First Dance wedding planning app',
        heroIntro:
            'Chasing RSVPs is nobody’s idea of fun. First Dance sends each guest a personal, secure RSVP link — and their response, meal choices and dietary needs flow straight back into your plan.',
        benefits: [
            {
                title: 'Personal link for every guest',
                text: 'Each guest gets their own RSVP link and a 6-digit code, shared in one tap over WhatsApp — no paper, no stamps, no lost replies.',
            },
            {
                title: 'Meal choices collected for you',
                text: 'All-day guests pick their starter, main and dessert from your menu as they RSVP. Evening guests skip the bits they don’t need.',
            },
            {
                title: 'Plus-one details too',
                text: 'Guests confirm their plus-ones’ names, ages and meals in the same flow, so your numbers are always complete.',
            },
            {
                title: 'Responses sync instantly',
                text: 'Accepts and declines update your guest list, dashboard chart and table plan the moment a guest replies.',
            },
            {
                title: 'Secure by design',
                text: 'RSVP links are unique per guest and protected with a one-time code, so only your guests can respond.',
            },
        ],
        howItWorks: [
            'Build your guest list and menu in the app.',
            'Send each guest their personal RSVP link via WhatsApp.',
            'Guests accept or decline, pick meals and confirm plus-ones online.',
            'Watch your dashboard update in real time as replies arrive.',
        ],
        faqs: [
            {
                q: 'How do guests receive their RSVP?',
                a: 'You share each guest’s personal link straight from the app — most couples send them over WhatsApp. Guests open the link, enter their code and reply in under a minute.',
            },
            {
                q: 'Do digital RSVPs need a premium plan?',
                a: 'Yes — digital RSVPs are part of the Diamond and Ever After plans, alongside your wedding website and guest photo album.',
            },
            {
                q: 'Can guests change their meal choices later?',
                a: 'Until they submit their final RSVP, guests can update their details. After that, you can adjust anything on their behalf inside the app.',
            },
        ],
        related: ['guest-list', 'wedding-website', 'menu-planner'],
    },
    {
        slug: 'wedding-website',
        name: 'Wedding Website Builder',
        plan: 'Premium',
        tagline: 'One beautiful link with everything your guests need',
        seoTitle: 'Wedding Website Builder UK | First Dance',
        seoDescription:
            'Create a beautiful personalised wedding website with your venue details, schedule, gift registry, FAQs and RSVP — no coding needed. Built from the First Dance app.',
        image: featureWebsite,
        imageAlt: 'Personalised wedding website built with the First Dance app',
        heroIntro:
            'Everything your guests need, all in one place. Build a personalised wedding website straight from the app — venue details, schedule, gift registry, FAQs and photo uploads — and share a single link.',
        benefits: [
            {
                title: 'Your own wedding URL',
                text: 'Choose a friendly link like firstdance.app/the-smiths-2026 and share it on invites, WhatsApp or your save the dates.',
            },
            {
                title: 'Four beautiful themes',
                text: 'Pick from First Dance Pink, Wine & Blush, Champagne or Sage Green to match your wedding colours.',
            },
            {
                title: 'All the details, no coding',
                text: 'Ceremony and reception info, travel and accommodation, dress code, bar details, gift registry and custom Q&A — all edited from your phone.',
            },
            {
                title: 'Built-in countdown and schedule',
                text: 'Your site shows a live countdown and the day’s schedule, using only the events you choose to make public.',
            },
            {
                title: 'QR code included',
                text: 'Download a QR code that points to your site — perfect for invitations, table signs and welcome boards.',
            },
        ],
        howItWorks: [
            'Fill in your details in the app — venue, times, gifts, FAQs and photos.',
            'Pick your colour theme and your custom URL.',
            'Preview your site any time from the Details tab.',
            'Upgrade to share it live with every guest.',
        ],
        faqs: [
            {
                q: 'Do I need to know how to code?',
                a: 'Not at all. Your website is generated from the details you add in the app, and updates instantly whenever you change something.',
            },
            {
                q: 'Is the wedding website included in the free plan?',
                a: 'You can build and preview your website for free. Sharing it live with guests is part of the Diamond and Ever After plans.',
            },
            {
                q: 'Can guests RSVP through the website?',
                a: 'Yes — your website links to your digital RSVPs, and guests can also upload photos and leave you a note on the day.',
            },
        ],
        related: ['rsvp', 'guest-photos', 'save-the-date'],
    },
    {
        slug: 'table-planner',
        name: 'Table & Seating Planner',
        plan: 'Free',
        tagline: 'Drag, drop and seat everyone happily',
        seoTitle: 'Wedding Table Plan App UK | First Dance',
        seoDescription:
            'Build your wedding seating plan on an interactive canvas. Round, long and square tables, a top table for the couple, and guests seated straight from your RSVP list.',
        image: featureTablePlan,
        imageAlt: 'Interactive wedding table planner with dancefloor in the First Dance app',
        heroIntro:
            'The puzzle every couple faces — solved with a playful, zoomable canvas. Arrange tables around your dancefloor, seat guests straight from your list, and shuffle until it feels right.',
        benefits: [
            {
                title: 'A visual, interactive room',
                text: 'Pinch to zoom and pan around your reception layout — top table, dancefloor, bar and cake stand included.',
            },
            {
                title: 'Any table shape',
                text: 'Round, long or square tables with seats arranged realistically around each shape, plus a classic top table for the two of you.',
            },
            {
                title: 'Seat straight from your guest list',
                text: 'Tap any empty chair to seat a guest. Anyone already seated elsewhere won’t appear twice — no double-booked chairs.',
            },
            {
                title: 'Grows with your plans',
                text: 'Add tables as RSVPs roll in; the room rearranges itself so the dancefloor always stays centre stage.',
            },
            {
                title: 'Free to try',
                text: 'Start your table plan on the free plan and upgrade when you need more tables for the full guest list.',
            },
        ],
        howItWorks: [
            'Add your guests and collect RSVPs.',
            'Create tables — choose shape, name and number of seats.',
            'Tap seats to assign guests, with the couple on the top table.',
            'Adjust as replies arrive; the layout keeps itself tidy.',
        ],
        faqs: [
            {
                q: 'Is the table planner free?',
                a: 'Yes — you can build your table plan on the free plan. Free accounts can add up to 4 tables; premium plans remove the limit.',
            },
            {
                q: 'Can I seat plus-ones?',
                a: 'Yes, plus-ones can be seated on any chair just like main guests, and dietary details travel with them.',
            },
            {
                q: 'What table shapes are supported?',
                a: 'Round, long (rectangular) and square guest tables, plus a traditional top table where the two of you always have pride of place.',
            },
        ],
        related: ['guest-list', 'menu-planner', 'rsvp'],
    },
    {
        slug: 'day-schedule',
        name: 'Wedding Day Schedule',
        plan: 'Free',
        tagline: 'From hair & makeup to the last dance',
        seoTitle: 'Wedding Day Schedule Planner UK | First Dance',
        seoDescription:
            'Plan your wedding day timeline from prep to first dance. Share a live schedule link with guests and suppliers so everyone knows where to be and when.',
        image: featureSchedule,
        imageAlt: 'Wedding day timeline and schedule in the First Dance planning app',
        heroIntro:
            'A relaxed wedding day is a well-timed one. Build your running order from classic wedding moments, keep timings at your fingertips, and share a live link so nobody has to ask “what time is…?”',
        benefits: [
            {
                title: 'Ready-made wedding moments',
                text: 'Pick from hair & makeup, the aisle walk, ceremony, wedding breakfast, speeches, cake cutting, first dance and more — then set your times.',
            },
            {
                title: 'You choose what guests see',
                text: 'Keep supplier logistics private and publish only the moments guests need — each event has a simple visibility toggle.',
            },
            {
                title: 'One live link for everyone',
                text: 'Share your schedule over WhatsApp with a firstdance.app link that always shows the latest version.',
            },
            {
                title: 'On your website too',
                text: 'Your public schedule appears on your wedding website automatically, right alongside the countdown.',
            },
        ],
        howItWorks: [
            'Add your wedding date in the app.',
            'Build your timeline from preset moments or your own events.',
            'Toggle which events are visible to guests.',
            'Share your schedule link — it updates live as plans change.',
        ],
        faqs: [
            {
                q: 'Is the schedule planner free?',
                a: 'Yes — building and sharing your day schedule is included in the free plan.',
            },
            {
                q: 'Can suppliers see the schedule?',
                a: 'Anyone with the link can view the public schedule — handy for photographers, bands and coordinators who need your timings.',
            },
        ],
        related: ['save-the-date', 'wedding-website', 'checklist'],
    },
    {
        slug: 'menu-planner',
        name: 'Menu & Meal Planner',
        plan: 'Free',
        tagline: 'Starters, mains and desserts — minus the spreadsheet',
        seoTitle: 'Wedding Menu Planner App UK | First Dance',
        seoDescription:
            'Create your wedding menu with dietary options, collect guest meal choices through digital RSVPs and export final numbers for your caterer. Free in First Dance.',
        image: mealIcon,
        imageAlt: 'Wedding menu and meal planning in the First Dance app',
        imageStyle: 'icon',
        heroIntro:
            'Build your wedding menu once, and let it do the rounds for you. Guests choose their courses as they RSVP, dietary needs are flagged clearly, and your caterer gets one tidy export.',
        benefits: [
            {
                title: 'Your full menu, course by course',
                text: 'Add starters, mains and desserts with descriptions, so guests know exactly what they’re choosing.',
            },
            {
                title: 'Dietary options built in',
                text: 'Mark dishes vegetarian (v), vegan (vg) or gluten free (gf) and capture allergen details in each description.',
            },
            {
                title: 'Choices collected via RSVP',
                text: 'All-day guests pick their meals as part of their digital RSVP — no chasing, no paper reply cards.',
            },
            {
                title: 'Caterer-ready export',
                text: 'Download a CSV of everyone’s meal choices and dietary requirements when final numbers are due.',
            },
        ],
        howItWorks: [
            'Create your menu items and flag dietary options.',
            'Send digital RSVPs to your guests.',
            'Guests choose their courses as they reply.',
            'Export the final meal counts for your caterer.',
        ],
        faqs: [
            {
                q: 'Is the meal planner free?',
                a: 'Yes — building your menu and tracking meal choices is included in the free plan. Collecting choices automatically via digital RSVP is part of the premium plans.',
            },
            {
                q: 'What about evening-only guests?',
                a: 'Evening guests aren’t asked for meal choices — the RSVP flow adapts automatically based on each guest’s invitation.',
            },
        ],
        related: ['rsvp', 'guest-list', 'table-planner'],
    },
    {
        slug: 'save-the-date',
        name: 'Digital Save the Dates',
        plan: 'Free',
        tagline: 'Share the news in one beautiful tap',
        seoTitle: 'Digital Save the Date App UK | First Dance',
        seoDescription:
            'Send beautiful digital save the dates over WhatsApp for free. A personalised link with your names and wedding date — invitation to follow. From the First Dance app.',
        image: featureSaveTheDate,
        imageAlt: 'Digital wedding save the date created in the First Dance app',
        heroIntro:
            'You’ve set the date — now tell everyone, the fun way. Share a beautiful personalised save-the-date link in seconds, completely free, straight from the app.',
        benefits: [
            {
                title: 'Beautiful and personal',
                text: 'Your names and wedding date on an elegant page, with “invitation to follow” — classic save-the-date charm without the printing costs.',
            },
            {
                title: 'One tap to share',
                text: 'Send your save-the-date link over WhatsApp to individuals or groups, right from your guest list.',
            },
            {
                title: 'Free for every couple',
                text: 'Digital save the dates are part of the free plan — a perfect first step the moment you get engaged.',
            },
            {
                title: 'Connected to everything else',
                text: 'When your wedding website goes live, guests already have you saved at the same firstdance.app home.',
            },
        ],
        howItWorks: [
            'Add your names and wedding date in the app.',
            'Preview your save-the-date page.',
            'Share your personal link over WhatsApp.',
            'Follow up with your website and RSVPs when you’re ready.',
        ],
        faqs: [
            {
                q: 'Are save the dates really free?',
                a: 'Yes — creating and sharing your digital save the date is completely free, with no guest limits.',
            },
            {
                q: 'When should we send save the dates?',
                a: 'Most UK couples send save the dates 9–12 months before the wedding — longer for destination weddings. Your formal invitation follows later.',
            },
        ],
        related: ['wedding-website', 'day-schedule', 'guest-list'],
    },
    {
        slug: 'checklist',
        name: 'Checklist & To-Do List',
        plan: 'Free',
        tagline: 'Know exactly what to do, and when',
        seoTitle: 'Wedding Planning Checklist App UK | First Dance',
        seoDescription:
            'Stay on track with a guided UK wedding planning checklist plus your own personal to-do list. From “12 months to go” to the big day itself — free in First Dance.',
        image: featureChecklist,
        imageAlt: 'Wedding planning checklist and to-do list in the First Dance app',
        heroIntro:
            'Wedding planning is a hundred small jobs — First Dance turns them into a game you can actually win. Follow the guided timeline checklist, add your own to-dos, and enjoy ticking things off.',
        benefits: [
            {
                title: 'A guided timeline',
                text: 'A ready-made checklist based on a typical UK wedding timeline, organised from over a year out to “on the day”.',
            },
            {
                title: 'Your own to-do list',
                text: 'Add personal tasks, big or small, and see your top three right on your dashboard so nothing slips.',
            },
            {
                title: 'The Aisle Walk',
                text: 'A playful stepping-stone journey through 13 planning milestones — from setting your budget to booking the honeymoon.',
            },
            {
                title: 'Momentum you can feel',
                text: 'Completed tasks and milestones build a sense of progress that spreadsheets never will.',
            },
        ],
        howItWorks: [
            'Create your free account and set your wedding date.',
            'Work through the guided checklist at your own pace.',
            'Add personal to-dos as they come up.',
            'Watch your progress grow as the big day approaches.',
        ],
        faqs: [
            {
                q: 'When should I start planning my wedding?',
                a: 'Many UK couples start 12–18 months out, but the checklist adapts to however long you have — it simply shows what matters most next.',
            },
            {
                q: 'Is the checklist free?',
                a: 'Yes — the guided checklist, personal to-do list and Aisle Walk journey are all part of the free plan.',
            },
        ],
        related: ['day-schedule', 'guest-list', 'save-the-date'],
    },
    {
        slug: 'guest-photos',
        name: 'Guest Photo Album',
        plan: 'Premium',
        tagline: 'Every candid moment, captured for you',
        seoTitle: 'Wedding Guest Photo Sharing App UK | First Dance',
        seoDescription:
            'Collect wedding photos from your guests in one private album. Guests upload photos and messages through your wedding website — no app download needed.',
        image: featureGuestGallery,
        imageAlt: 'Guests uploading wedding photos to a shared album via First Dance',
        heroIntro:
            'Your photographer can’t be everywhere. Let your guests fill the gaps — they upload photos and messages through your wedding website on the day, and everything lands in your private album.',
        benefits: [
            {
                title: 'No app needed for guests',
                text: 'Guests upload straight from your wedding website on their phones — no downloads, no accounts, no fuss.',
            },
            {
                title: 'Photos and notes together',
                text: 'Each guest can share photos along with a personal message, so you get the story behind the snap.',
            },
            {
                title: 'One private album',
                text: 'Everything collects in your own gallery in the app — review submissions and relive the day whenever you like.',
            },
            {
                title: 'QR code friendly',
                text: 'Pop your website QR code on tables and signage so uploading is one scan away all night.',
            },
        ],
        howItWorks: [
            'Go live with your wedding website on a premium plan.',
            'Share your link or QR code with guests on the day.',
            'Guests upload photos and leave you notes.',
            'Browse your private album in the app after the big day.',
        ],
        faqs: [
            {
                q: 'Do guests need the app to upload photos?',
                a: 'No — guests upload through your wedding website in their browser. It works on any phone.',
            },
            {
                q: 'Who can see the photos?',
                a: 'Uploads go to your private album in the app. You choose what, if anything, gets shared more widely.',
            },
        ],
        related: ['wedding-website', 'rsvp', 'save-the-date'],
    },
]

export default FEATURES

export const getFeatureBySlug = (slug) =>
    FEATURES.find((feature) => feature.slug === slug) || null
